import React, { useState, useEffect, useCallback } from 'react'
import { 
  CheckCircleIcon, 
  ExclamationTriangleIcon, 
  ArrowPathIcon,
  XMarkIcon,
  ClockIcon,
  ClipboardDocumentIcon,
  PlusIcon
} from '@heroicons/react/24/outline'
import massSyncService, { SyncProgress, SyncResult } from '../../api/massSyncService'
import CreateMissingProductsModal from './CreateMissingProductsModal'

interface MassSyncModalProps {
  isOpen: boolean
  onClose: () => void
  onSyncComplete: (result: SyncResult) => void
  ofimaProducts?: any[] // Productos de Ofima para crear productos faltantes
}

const MassSyncModal: React.FC<MassSyncModalProps> = ({
  isOpen,
  onClose,
  onSyncComplete,
  ofimaProducts = []
}) => {
  const [currentStep, setCurrentStep] = useState<'idle' | 'syncing' | 'completed' | 'error'>('idle')
  const [progress, setProgress] = useState(0)
  const [syncProgress, setSyncProgress] = useState<SyncProgress | null>(null)
  const [result, setResult] = useState<SyncResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const [copiedSkus, setCopiedSkus] = useState(false)
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)

  // Función para obtener el icono del paso actual
  const getStepIcon = (step: string) => {
    switch (step) {
      case 'idle':
        return <ArrowPathIcon className="h-8 w-8 text-gray-400" />
      case 'syncing':
        return <ArrowPathIcon className="h-8 w-8 text-blue-500 animate-spin" />
      case 'completed':
        return <CheckCircleIcon className="h-8 w-8 text-green-500" />
      case 'error':
        return <ExclamationTriangleIcon className="h-8 w-8 text-red-500" />
      default:
        return <ClockIcon className="h-8 w-8 text-gray-400" />
    }
  }

  // Función para obtener el texto del paso actual
  const getStepText = (step: string) => {
    switch (step) {
      case 'idle':
        return 'Preparando sincronización...'
      case 'syncing':
        return 'Sincronizando productos...'
      case 'completed':
        return 'Sincronización completada'
      case 'error':
        return 'Error en la sincronización'
      default:
        return 'Procesando...'
    }
  }

  // Función para ejecutar la sincronización
  const handleSync = useCallback(async () => {
    if (isProcessing) return

    setIsProcessing(true)
    setCurrentStep('syncing')
    setProgress(0)
    setError(null)
    setResult(null)

    try {
      const syncResult = await massSyncService.syncAllProducts((progressUpdate) => {
        setSyncProgress(progressUpdate)
        setProgress(Math.round((progressUpdate.processedProducts / progressUpdate.totalProducts) * 100))
      })

      setResult(syncResult)
      
      if (syncResult.success) {
        setCurrentStep('completed')
        onSyncComplete(syncResult)
      } else {
        setCurrentStep('error')
        setError(syncResult.message)
      }

    } catch (error: any) {
      console.error('Error en sincronización masiva:', error)
      setCurrentStep('error')
      setError(error.message || 'Error inesperado durante la sincronización')
    } finally {
      setIsProcessing(false)
    }
  }, [isProcessing, onSyncComplete])

  // Ejecutar sincronización automáticamente al abrir el modal
  useEffect(() => {
    if (isOpen && currentStep === 'idle') {
      handleSync()
    }
  }, [isOpen, currentStep, handleSync])

  // Función para extraer SKU de un mensaje de error
  const extractSkuFromError = (errorText: string): string | null => {
    // Patrones para extraer SKUs de diferentes formatos de error
    const patterns = [
      /Producto no encontrado con ID: (.+)/,
      /Producto no encontrado con SKU: (.+)/,
      /Error con ID: (.+)/,
      /Error con SKU: (.+)/
    ]
    
    for (const pattern of patterns) {
      const match = errorText.match(pattern)
      if (match && match[1]) {
        return match[1].trim()
      }
    }
    
    return null
  }

  // Función para extraer SKUs de los errores
  const getErrorSkus = (): string[] => {
    if (!result || !result.errors || result.errors.length === 0) {
      return []
    }

    const extractedSkus: string[] = []
    
    result.errors.forEach((error) => {
      let sku: string | null = null
      
      // Si el error tiene estructura {sku, error}
      if (error.sku) {
        sku = error.sku
      } 
      // Si el error es un string, intentar extraer el SKU
      else if (typeof error === 'string') {
        sku = extractSkuFromError(error)
      }
      // Si el error tiene estructura {error: "mensaje"}
      else if (error.error && typeof error.error === 'string') {
        sku = extractSkuFromError(error.error)
      }
      
      if (sku && sku.trim() !== '') {
        extractedSkus.push(sku.trim())
      }
    })
    
    // Filtrar SKUs únicos y válidos
    return Array.from(new Set(extractedSkus))
      .filter(sku => sku && sku.trim() !== '')
  }

  // Función para copiar SKUs con error
  const handleCopyErrorSkus = async () => {
    if (!result || !result.errors || result.errors.length === 0) {
      console.log('No hay errores para copiar')
      return
    }

    console.log('Errores originales:', result.errors)
    
    // Extraer SKUs de los mensajes de error
    const extractedSkus: string[] = []
    
    result.errors.forEach((error, index) => {
      let sku: string | null = null
      
      // Si el error tiene estructura {sku, error}
      if (error.sku) {
        sku = error.sku
      } 
      // Si el error es un string, intentar extraer el SKU
      else if (typeof error === 'string') {
        sku = extractSkuFromError(error)
      }
      // Si el error tiene estructura {error: "mensaje"}
      else if (error.error && typeof error.error === 'string') {
        sku = extractSkuFromError(error.error)
      }
      
      if (sku && sku.trim() !== '') {
        extractedSkus.push(sku.trim())
        console.log(`Error ${index}: "${error}" -> SKU extraído: "${sku}"`)
      } else {
        console.log(`Error ${index}: "${error}" -> No se pudo extraer SKU`)
      }
    })
    
    console.log('SKUs extraídos:', extractedSkus)
    
    // Filtrar SKUs únicos y válidos
    const uniqueValidSkus = Array.from(new Set(extractedSkus))
      .filter(sku => sku && sku.trim() !== '')
      .join(', ')
    
    console.log('SKUs válidos finales:', uniqueValidSkus)
    
    // Si no hay SKUs válidos, mostrar mensaje y no hacer nada
    if (!uniqueValidSkus || uniqueValidSkus.trim() === '') {
      console.log('No hay SKUs válidos para copiar')
      alert('No se pudieron extraer SKUs válidos de los errores')
      return
    }
    
    try {
      // Verificar si el navegador soporta la Clipboard API
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(uniqueValidSkus)
        console.log('SKUs copiados exitosamente:', uniqueValidSkus)
        setCopiedSkus(true)
        // Ocultar el mensaje después de 2 segundos
        setTimeout(() => setCopiedSkus(false), 2000)
      } else {
        // Fallback para navegadores que no soportan clipboard API
        const textArea = document.createElement('textarea')
        textArea.value = uniqueValidSkus
        textArea.style.position = 'fixed'
        textArea.style.left = '-999999px'
        textArea.style.top = '-999999px'
        document.body.appendChild(textArea)
        textArea.focus()
        textArea.select()
        
        try {
          document.execCommand('copy')
          console.log('SKUs copiados exitosamente (fallback):', uniqueValidSkus)
          setCopiedSkus(true)
          // Ocultar el mensaje después de 2 segundos
          setTimeout(() => setCopiedSkus(false), 2000)
        } catch (err) {
          console.error('Error al copiar con fallback:', err)
          alert('No se pudo copiar al portapapeles')
        } finally {
          document.body.removeChild(textArea)
        }
      }
    } catch (err) {
      console.error('Error al copiar al portapapeles:', err)
      alert('No se pudo copiar al portapapeles')
    }
  }

  // Resetear estado al cerrar
  const handleClose = () => {
    setCurrentStep('idle')
    setProgress(0)
    setSyncProgress(null)
    setResult(null)
    setError(null)
    setIsProcessing(false)
    setCopiedSkus(false)
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 m-4 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h2 className=" ">
            Sincronización Masiva de Precios
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Progreso */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">
              {getStepText(currentStep)}
            </span>
            <span className="text-sm text-gray-500">{progress}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Estado actual */}
        <div className="flex items-center gap-3 mb-6">
          {getStepIcon(currentStep)}
          <span className="text-lg font-medium">
            {getStepText(currentStep)}
          </span>
        </div>

        {/* Información de progreso detallada */}
        {syncProgress && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-medium text-blue-800">Lote actual:</span>
                <span className="ml-2 text-blue-700">
                  {syncProgress.currentBatch} de {syncProgress.totalBatches}
                </span>
              </div>
              <div>
                <span className="font-medium text-blue-800">Productos procesados:</span>
                <span className="ml-2 text-blue-700">
                  {syncProgress.processedProducts} de {syncProgress.totalProducts}
                </span>
              </div>
              <div>
                <span className="font-medium text-green-800">Exitosos:</span>
                <span className="ml-2 text-green-700">{syncProgress.successCount}</span>
              </div>
              <div>
                <span className="font-medium text-red-800">Errores:</span>
                <span className="ml-2 text-red-700">{syncProgress.errorCount}</span>
              </div>
            </div>
          </div>
        )}


        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
            <div className="flex items-start gap-3">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <span className="text-sm font-medium text-red-800 block mb-1">
                  Error en la Sincronización
                </span>
                <span className="text-sm text-red-700">{error}</span>
              </div>
            </div>
          </div>
        )}

        {/* Lista de errores detallados */}
        {result && result.errors && result.errors.length > 0 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
            <div className="flex items-start gap-3">
              <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-yellow-800">
                    Errores Detallados ({result.errors.length})
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setIsCreateModalOpen(true)}
                      className="flex items-center gap-1 px-2 py-1 text-xs rounded transition-colors bg-blue-100 hover:bg-blue-200 text-blue-700"
                      title="Crear productos faltantes"
                    >
                      <PlusIcon className="h-4 w-4" />
                      Crear Productos Faltantes
                    </button>
                    <button
                      onClick={handleCopyErrorSkus}
                      className={`flex items-center gap-1 px-2 py-1 text-xs rounded transition-colors ${
                        copiedSkus 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-yellow-100 hover:bg-yellow-200 text-yellow-700'
                      }`}
                      title={copiedSkus ? "SKUs copiados" : "Copiar SKUs con error"}
                    >
                      <ClipboardDocumentIcon className="h-4 w-4" />
                      {copiedSkus ? '¡Copiado!' : 'Copiar SKUs'}
                    </button>
                  </div>
                </div>
                <div className="max-h-32 overflow-y-auto">
                  {result.errors.slice(0, 10).map((error, index) => (
                    <div key={index} className="text-xs text-yellow-700 mb-1">
                      <span className="font-medium">{error.sku}:</span> {error.error}
                    </div>
                  ))}
                  {result.errors.length > 10 && (
                    <div className="text-xs text-yellow-600 italic">
                      ... y {result.errors.length - 10} errores más
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Botones de acción */}
        <div className="flex justify-end gap-3">
          {currentStep === 'completed' && (
            <button
              onClick={handleClose}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              Cerrar
            </button>
          )}
          
          {currentStep === 'error' && (
            <>
              <button
                onClick={handleClose}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
              >
                Cerrar
              </button>
              <button
                onClick={handleSync}
                disabled={isProcessing}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                Reintentar
              </button>
            </>
          )}
        </div>
      </div>

      {/* Modal para crear productos faltantes */}
      <CreateMissingProductsModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        errorSkus={getErrorSkus()}
        ofimaProducts={ofimaProducts}
      />
    </div>
  )
}

export default MassSyncModal
